import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { blobToWavFile } from '../lib/audio';
import { CardTitle, Icon, Spinner, Waveform } from '../components/ui';

interface Voice {
  id: string;
  name: string;
  provider: string;
  language: string;
  gender: string;
  preview_url?: string;
  styles?: string[];
  age?: string;
  is_custom?: boolean;
  custom_voice_id?: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  murf: 'Murf',
  elevenlabs: 'ElevenLabs',
  openai: 'OpenAI',
  fish: 'Fish',
};

const CLONE_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'tr', label: 'Turkish' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ru', label: 'Russian' },
];

// Guided reading scripts (~45–60s read aloud, phonetically varied). The text
// read is also sent as the transcript, which improves clone accuracy.
const CLONE_SCRIPTS: Record<string, string> = {
  en: "Hi! This is my natural speaking voice, and I'm recording it to create my personal voice clone. I'll speak clearly, at my own pace, the way I talk every day. Sometimes I ask a question — can you hear the difference? Then I pause, take a breath, and carry on in a calm, steady tone. Numbers like three, twenty-seven, and nineteen ninety-five help capture how I pronounce digits. Words like ocean, whisper, thunder, and morning add variety. When I'm done, this recording will let me turn anything I write into audio that sounds exactly like me.",
  es: '¡Hola! Esta es mi voz natural, y la estoy grabando para crear mi clon de voz personal. Hablaré con claridad, a mi propio ritmo, tal como hablo todos los días. A veces hago una pregunta — ¿se nota la diferencia? Luego hago una pausa, respiro y continúo con un tono tranquilo y constante. Números como tres, veintisiete y mil novecientos noventa y cinco ayudan a capturar cómo pronuncio las cifras. Palabras como océano, susurro, trueno y mañana agregan variedad. Al terminar, esta grabación me permitirá convertir todo lo que escriba en audio que suena exactamente como yo.',
  fr: "Bonjour ! Voici ma voix naturelle, que j'enregistre pour créer mon clone vocal personnel. Je parle clairement, à mon rythme, comme je le fais chaque jour. Parfois je pose une question — entendez-vous la différence ? Puis je fais une pause, je respire, et je continue d'un ton calme et régulier. Des nombres comme trois, vingt-sept et mille neuf cent quatre-vingt-quinze aident à capturer ma prononciation des chiffres. Des mots comme océan, murmure, tonnerre et matin ajoutent de la variété. Une fois terminé, cet enregistrement me permettra de transformer tout ce que j'écris en audio qui me ressemble exactement.",
  de: 'Hallo! Das ist meine natürliche Stimme, die ich aufnehme, um meinen persönlichen Stimmklon zu erstellen. Ich spreche klar und in meinem eigenen Tempo, so wie ich jeden Tag spreche. Manchmal stelle ich eine Frage — hört man den Unterschied? Dann mache ich eine Pause, atme durch und fahre in einem ruhigen, gleichmäßigen Ton fort. Zahlen wie drei, siebenundzwanzig und neunzehnhundertfünfundneunzig zeigen, wie ich Ziffern ausspreche. Wörter wie Ozean, Flüstern, Donner und Morgen sorgen für Abwechslung. Diese Aufnahme wird es mir ermöglichen, alles, was ich schreibe, in Audio zu verwandeln, das genau wie ich klingt.',
  it: 'Ciao! Questa è la mia voce naturale, e la sto registrando per creare il mio clone vocale personale. Parlo in modo chiaro, al mio ritmo, proprio come parlo ogni giorno. A volte faccio una domanda — si sente la differenza? Poi faccio una pausa, respiro e continuo con un tono calmo e costante. Numeri come tre, ventisette e millenovecentonovantacinque aiutano a catturare come pronuncio le cifre. Parole come oceano, sussurro, tuono e mattina aggiungono varietà. Alla fine, questa registrazione mi permetterà di trasformare tutto ciò che scrivo in audio che suona esattamente come me.',
  pt: 'Olá! Esta é a minha voz natural, e estou gravando para criar o meu clone de voz pessoal. Falo com clareza, no meu ritmo, do jeito que falo todos os dias. Às vezes faço uma pergunta — dá para notar a diferença? Depois faço uma pausa, respiro e continuo em um tom calmo e constante. Números como três, vinte e sete e mil novecentos e noventa e cinco ajudam a capturar como pronuncio os dígitos. Palavras como oceano, sussurro, trovão e manhã adicionam variedade. No final, esta gravação vai me permitir transformar tudo o que eu escrever em áudio que soa exatamente como eu.',
};

const REC_MAX_SECONDS = 120;
const REC_MIN_SECONDS = 10;

// Fallback used only while tier limits are loading.
const DEFAULT_MAX_CHARACTERS = 5000;

// API-accepted setting ranges (apps/api providers: speed 0.5–2.0, pitch -20–20).
const SPEED_MIN = 0.5;
const SPEED_MAX = 2;
const PITCH_MIN = -20;
const PITCH_MAX = 20;

// All output formats the platform supports; availability is tier-gated.
const ALL_FORMATS = ['mp3', 'wav', 'ogg'];

// Only Murf maps our pitch setting to the provider; others ignore it.
const PITCH_PROVIDERS = new Set(['murf']);

// Stop polling after ~5 minutes (150 attempts at 2s).
const MAX_POLL_ATTEMPTS = 150;

interface GenerationJob {
  job_id: string;
  status: string;
  progress: number;
  audio_file?: {
    url: string;
    format: string;
    duration_seconds: number;
  };
  error?: {
    code: string;
    message: string;
  };
}

// Stable avatar color derived from provider + name hash (radial-gradient like the comp).
const AVATAR_COLORS = ['#1F8FE0', '#D8362F', '#2E8B57', '#E0A21F', '#8A5CF6', '#14B8A6', '#E05C8A', '#767680'];

function avatarColor(voice: Voice): string {
  const key = `${voice.provider}:${voice.name}`;
  let h = 7;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) % 9973;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function voiceKey(voice: Voice): string {
  return `${voice.provider}-${voice.id}`;
}

export function Generate() {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(false);
  const [voicesError, setVoicesError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState<GenerationJob | null>(null);
  // Smoothed progress: the server only reports milestones (0 queued, 10 picked
  // up, 70 audio generated, 100 done), so between polls we creep toward the
  // next milestone's cap to keep the bar moving during long provider calls.
  const [displayProgress, setDisplayProgress] = useState(0);
  const [error, setError] = useState('');
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollGenRef = useRef(0);

  // Tier limits + monthly usage (C1)
  const [tierLimits, setTierLimits] = useState<{
    max_text_length: number;
    output_formats: string[];
  } | null>(null);
  const [remainingChars, setRemainingChars] = useState<number | null>(null);
  const [limitChars, setLimitChars] = useState<number | null>(null);

  // Generation settings (C2)
  const [speed, setSpeed] = useState(1);
  const [pitch, setPitch] = useState(0);
  const [format, setFormat] = useState('mp3');
  const [audioName, setAudioName] = useState('');

  // Filters
  const [providerFilter, setProviderFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [search, setSearch] = useState('');

  // Voice preview playback (one at a time)
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  // Lazily fetched sample URLs for voices without a provider-supplied preview
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState('');

  // Voice cloning
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneLanguage, setCloneLanguage] = useState('en');
  const [cloneMode, setCloneMode] = useState<'record' | 'upload'>('record');
  const [cloneFile, setCloneFile] = useState<File | null>(null);
  const [cloneTranscript, setCloneTranscript] = useState(CLONE_SCRIPTS.en);
  const [cloneConsent, setCloneConsent] = useState(false);
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [deletingCloneId, setDeletingCloneId] = useState<string | null>(null);

  // In-browser recording
  const [recState, setRecState] = useState<'idle' | 'recording' | 'processing' | 'done'>('idle');
  const [recSeconds, setRecSeconds] = useState(0);
  const [recUrl, setRecUrl] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const recStreamRef = useRef<MediaStream | null>(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recChunksRef = useRef<Blob[]>([]);

  // Generated audio playback
  const genAudioRef = useRef<HTMLAudioElement | null>(null);
  const [genPlaying, setGenPlaying] = useState(false);
  const [genTime, setGenTime] = useState(0);
  const [genDuration, setGenDuration] = useState(0);

  useEffect(() => {
    loadVoices();
    loadLimits();
    return () => {
      previewRef.current?.pause();
      previewRef.current = null;
      pollGenRef.current++; // stop any in-flight polling loop
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVoices = async () => {
    setLoading(true);
    setVoicesError('');
    const result = await api.getVoices();
    if (result.success && result.data) {
      setVoices(result.data);
      if (result.data.length > 0 && !selectedVoice) {
        setSelectedVoice(result.data[0]);
      }
    } else {
      setVoicesError(result.error?.message || "Couldn't load voices. Check your connection and try again.");
    }
    setLoading(false);
  };

  const loadLimits = async () => {
    const [tiersResult, usageResult] = await Promise.all([api.getTiers(), api.getUsage()]);
    if (tiersResult.success && tiersResult.data) {
      const myTier =
        tiersResult.data.find((t) => t.id === user?.tier.id) ||
        tiersResult.data.find((t) => t.slug === user?.tier.slug);
      if (myTier) {
        setTierLimits({
          max_text_length: myTier.limits.max_text_length,
          output_formats: myTier.output_formats,
        });
      }
    }
    if (usageResult.success && usageResult.data) {
      setRemainingChars(usageResult.data.characters.remaining);
      setLimitChars(usageResult.data.characters.limit);
    }
  };

  const filteredVoices = voices.filter((voice) => {
    if (providerFilter && voice.provider !== providerFilter) return false;
    if (languageFilter && voice.language !== languageFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !voice.name.toLowerCase().includes(q) &&
        !voice.language.toLowerCase().includes(q) &&
        !voice.provider.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  const providers = [...new Set(voices.map((v) => v.provider))];
  const languages = [...new Set(voices.map((v) => v.language).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );

  const handleGenerate = async () => {
    if (!text.trim() || !selectedVoice) return;

    setError('');
    setPollTimedOut(false);
    setGenerating(true);
    setCurrentJob(null);
    setDisplayProgress(0);
    setGenPlaying(false);
    setGenTime(0);
    setGenDuration(0);

    const settings: { speed?: number; pitch?: number; format?: string; voice_name?: string } = {};
    if (speed !== 1) settings.speed = speed;
    if (pitch !== 0 && PITCH_PROVIDERS.has(selectedVoice.provider)) settings.pitch = pitch;
    if (format !== 'mp3') settings.format = format;
    // Friendly voice name so the library shows it instead of the raw voice id
    if (selectedVoice.name) settings.voice_name = selectedVoice.name;

    const result = await api.generateAudio({
      text: text.trim(),
      voice_id: selectedVoice.id,
      provider: selectedVoice.provider,
      ...(audioName.trim() ? { name: audioName.trim() } : {}),
      ...(Object.keys(settings).length > 0 ? { settings } : {}),
    });

    if (!result.success) {
      setError(result.error?.message || 'Failed to start generation');
      setGenerating(false);
      return;
    }

    if (result.data) {
      // Start polling for job status
      pollJobStatus(result.data.job_id);
    }
  };

  const pollJobStatus = async (jobId: string) => {
    const gen = ++pollGenRef.current;
    let attempts = 0;

    const poll = async () => {
      if (gen !== pollGenRef.current) return; // superseded or cancelled
      const result = await api.getJob(jobId);
      if (gen !== pollGenRef.current) return;

      if (result.success && result.data) {
        setCurrentJob(result.data);

        const server = result.data.progress ?? 0;
        const cap =
          result.data.status === 'completed' ? 100
          : server >= 70 ? 95   // stored + billed, finishing up
          : server >= 10 ? 65   // provider is generating (the long step)
          : 8;                  // still queued
        setDisplayProgress((prev) => Math.max(server, Math.min(prev + 2, cap)));

        if (['completed', 'failed', 'cancelled'].includes(result.data.status)) {
          setGenerating(false);
          if (result.data.status === 'completed') loadLimits(); // refresh remaining quota
          return;
        }

        attempts++;
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setPollTimedOut(true);
          setGenerating(false);
          return;
        }

        // Continue polling
        setTimeout(poll, 2000);
      } else {
        setError('Failed to check job status');
        setGenerating(false);
      }
    };

    poll();
  };

  const handleCancelJob = async () => {
    if (!currentJob || currentJob.status !== 'queued' || cancelling) return;
    setCancelling(true);
    const result = await api.cancelJob(currentJob.job_id);
    setCancelling(false);
    if (result.success) {
      pollGenRef.current++; // stop the polling loop
      setGenerating(false);
      setCurrentJob(null);
    } else {
      setError(result.error?.message || 'Failed to cancel the job');
    }
  };

  const playPreviewUrl = (key: string, url: string) => {
    const audio = new Audio(url);
    audio.onended = () => setPreviewingId((cur) => (cur === key ? null : cur));
    audio.onerror = () => setPreviewingId((cur) => (cur === key ? null : cur));
    previewRef.current = audio;
    setPreviewingId(key);
    audio.play().catch(() => setPreviewingId((cur) => (cur === key ? null : cur)));
  };

  const togglePreview = async (voice: Voice) => {
    const key = voiceKey(voice);
    if (previewingId === key) {
      previewRef.current?.pause();
      previewRef.current = null;
      setPreviewingId(null);
      return;
    }
    previewRef.current?.pause();
    previewRef.current = null;
    setPreviewingId(null);
    setPreviewError('');

    const knownUrl = voice.preview_url || previewUrls[key];
    if (knownUrl) {
      playPreviewUrl(key, knownUrl);
      return;
    }

    // No provider-supplied sample — ask the API to generate one (cached server-side)
    if (previewLoading) return;
    setPreviewLoading(key);
    const result = await api.getVoicePreview(voice.id, voice.provider);
    setPreviewLoading(null);
    if (result.success && result.data?.preview_url) {
      const url = result.data.preview_url;
      setPreviewUrls((cur) => ({ ...cur, [key]: url }));
      playPreviewUrl(key, url);
    } else {
      setPreviewError(result.error?.message || `Couldn't load a preview for ${voice.name}`);
    }
  };

  const toggleGenPlay = () => {
    const audio = genAudioRef.current;
    if (!audio) return;
    if (genPlaying) {
      audio.pause();
    } else {
      // Stop any voice preview so only one thing plays at a time.
      previewRef.current?.pause();
      previewRef.current = null;
      setPreviewingId(null);
      audio.play().catch(() => setGenPlaying(false));
    }
  };

  const stopRecordingHardware = () => {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
    recStreamRef.current?.getTracks().forEach((t) => t.stop());
    recStreamRef.current = null;
    recRef.current = null;
  };

  const resetRecording = () => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    stopRecordingHardware();
    if (recUrl) URL.revokeObjectURL(recUrl);
    setRecUrl(null);
    setRecState('idle');
    setRecSeconds(0);
    setCloneFile(null);
  };

  const startRecording = async () => {
    setCloneError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setCloneError('Recording is not supported in this browser — upload a file instead.');
      setCloneMode('upload');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      recRef.current = recorder;
      recChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stopRecordingHardware();
        setRecState('processing');
        try {
          const blob = new Blob(recChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
          const wav = await blobToWavFile(blob);
          setCloneFile(wav);
          setRecUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return URL.createObjectURL(wav);
          });
          setRecState('done');
        } catch {
          setCloneError('Could not process the recording. Try again or upload a file.');
          setRecState('idle');
        }
      };
      recorder.start();
      setRecState('recording');
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        setRecSeconds((s) => {
          if (s + 1 >= REC_MAX_SECONDS && recRef.current?.state === 'recording') {
            recRef.current.stop();
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      setCloneError('Microphone access was denied. Allow it in your browser, or upload a file instead.');
    }
  };

  const stopRecording = () => {
    if (recRef.current?.state === 'recording') {
      if (recSeconds < REC_MIN_SECONDS) {
        setCloneError(`Keep going — record at least ${REC_MIN_SECONDS} seconds.`);
        return;
      }
      recRef.current.stop();
    }
  };

  const closeCloneModal = () => {
    if (cloneBusy) return;
    resetRecording();
    setCloneOpen(false);
  };

  const changeCloneLanguage = (code: string) => {
    setCloneLanguage(code);
    // Keep the transcript in sync with the reading script unless the user
    // already replaced it with their own text.
    setCloneTranscript((cur) => {
      const wasScript = !cur.trim() || Object.values(CLONE_SCRIPTS).includes(cur);
      return wasScript ? CLONE_SCRIPTS[code] || '' : cur;
    });
  };

  const submitClone = async () => {
    if (cloneBusy) return;
    setCloneError('');
    if (!cloneName.trim()) {
      setCloneError('Give your voice a name.');
      return;
    }
    if (!cloneFile) {
      setCloneError(
        cloneMode === 'record'
          ? 'Record your voice first — read the script above while recording.'
          : 'Choose an audio sample (30–120 seconds of clear speech works best).'
      );
      return;
    }
    if (!cloneConsent) {
      setCloneError('Please confirm you own this voice or have permission to clone it.');
      return;
    }
    setCloneBusy(true);
    const result = await api.createCustomVoice({
      name: cloneName.trim(),
      sample: cloneFile,
      consent: cloneConsent,
      language: cloneLanguage,
      transcript: cloneTranscript.trim() || undefined,
    });
    setCloneBusy(false);
    if (result.success) {
      resetRecording();
      setCloneOpen(false);
      setCloneName('');
      setCloneFile(null);
      setCloneConsent(false);
      setCloneTranscript(CLONE_SCRIPTS[cloneLanguage] || '');
      await loadVoices();
    } else {
      setCloneError(result.error?.message || 'Cloning failed. Try a different sample.');
    }
  };

  const deleteClone = async (voice: Voice) => {
    if (!voice.custom_voice_id || deletingCloneId) return;
    if (!window.confirm(`Delete your cloned voice “${voice.name}”? This can't be undone.`)) return;
    setDeletingCloneId(voice.custom_voice_id);
    const result = await api.deleteCustomVoice(voice.custom_voice_id);
    setDeletingCloneId(null);
    if (result.success) {
      if (selectedVoice?.id === voice.id) setSelectedVoice(null);
      await loadVoices();
    } else {
      setPreviewError(result.error?.message || 'Failed to delete voice');
    }
  };

  const resetForAnother = () => {
    genAudioRef.current?.pause();
    setCurrentJob(null);
    setText('');
    setPollTimedOut(false);
    setGenPlaying(false);
    setGenTime(0);
    setGenDuration(0);
  };

  const characterCount = text.length;
  const maxCharacters = tierLimits?.max_text_length ?? DEFAULT_MAX_CHARACTERS;
  const overQuota = remainingChars !== null && characterCount > remainingChars;
  const pitchSupported = selectedVoice ? PITCH_PROVIDERS.has(selectedVoice.provider) : false;
  const allowedFormats = tierLimits?.output_formats ?? ['mp3'];
  const formatOptions = [...new Set([...ALL_FORMATS, ...allowedFormats])];
  const lockedFormats = formatOptions.filter((f) => !allowedFormats.includes(f));

  const progress = Math.min(100, Math.max(0, Math.round(
    currentJob?.status === 'completed' ? 100 : displayProgress
  )));
  const showProgress = generating && currentJob?.status !== 'completed' && currentJob?.status !== 'failed';
  const completed = currentJob?.status === 'completed' && currentJob.audio_file;
  const failed = currentJob?.status === 'failed';
  const displayDuration = currentJob?.audio_file?.duration_seconds || genDuration;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5 max-w-[1180px] items-start">
      {/* ------------------------------ Left column ------------------------------ */}
      <div className="flex flex-col gap-4">
        {/* Script card */}
        <div className="card p-[22px]">
          <div className="flex justify-between items-center mb-3">
            <label className="text-mist-300 text-[13.5px] font-semibold">Your script</label>
            <span className="font-mono text-xs text-mist-500">Murf · ElevenLabs · OpenAI</span>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={maxCharacters}
            placeholder="Type or paste your text here…"
            className="w-full min-h-[280px] resize-y p-4 rounded-xl border border-ink-700 bg-ink-950 text-mist-200 text-[15px] leading-[1.7] outline-none focus:border-[var(--a-core)] transition-colors"
          />
          <div className="flex justify-between items-center mt-3 gap-3 flex-wrap">
            <span
              className="text-[13px]"
              style={{ color: characterCount > maxCharacters * 0.9 ? '#F0928C' : '#767680' }}
            >
              {characterCount.toLocaleString()} / {maxCharacters.toLocaleString()} characters
            </span>
            <div className="flex items-center gap-4">
              {remainingChars !== null && limitChars !== null && (
                <span className="text-[13px]" style={{ color: overQuota ? '#F0928C' : '#767680' }}>
                  {remainingChars.toLocaleString()} of {limitChars.toLocaleString()} characters left this month
                </span>
              )}
              <button
                onClick={() => setText('')}
                className="bg-transparent border-none text-mist-500 hover:text-mist-300 text-[13px] font-semibold cursor-pointer transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {overQuota && (
            <div
              className="mt-3 p-3 rounded-xl text-[13px]"
              style={{ background: 'rgba(224, 162, 31, .12)', color: '#E8C377' }}
            >
              This script needs {characterCount.toLocaleString()} characters, but you only have{' '}
              {(remainingChars ?? 0).toLocaleString()} left this month. Shorten the text or upgrade your plan.
            </div>
          )}

          {/* Audio name (optional) */}
          <div className="mt-4">
            <label htmlFor="gen-name" className="block text-mist-400 text-[12.5px] font-semibold mb-1.5">
              Audio name <span className="font-normal text-mist-500">(optional)</span>
            </label>
            <input
              id="gen-name"
              type="text"
              maxLength={120}
              value={audioName}
              onChange={(e) => setAudioName(e.target.value)}
              placeholder="e.g. Episode 12 intro"
              className="input w-full !py-2 text-[13px]"
            />
          </div>

          {/* Voice settings */}
          <div className="mt-4 pt-4 border-t border-ink-800 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label htmlFor="gen-speed" className="text-mist-400 text-[12.5px] font-semibold">
                  Speed
                </label>
                <span className="font-mono text-[12px] text-mist-500">{speed.toFixed(2)}×</span>
              </div>
              <input
                id="gen-speed"
                type="range"
                min={SPEED_MIN}
                max={SPEED_MAX}
                step={0.05}
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="w-full accent-[var(--a-core)] cursor-pointer"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label htmlFor="gen-pitch" className="text-mist-400 text-[12.5px] font-semibold">
                  Pitch
                </label>
                <span className="font-mono text-[12px] text-mist-500">
                  {pitchSupported ? (pitch > 0 ? `+${pitch}` : pitch) : 'n/a'}
                </span>
              </div>
              <input
                id="gen-pitch"
                type="range"
                min={PITCH_MIN}
                max={PITCH_MAX}
                step={1}
                value={pitch}
                onChange={(e) => setPitch(Number(e.target.value))}
                disabled={!pitchSupported}
                title={pitchSupported ? undefined : 'Pitch is only supported by Murf voices'}
                className="w-full accent-[var(--a-core)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label htmlFor="gen-format" className="block text-mist-400 text-[12.5px] font-semibold mb-1.5">
                Format
              </label>
              <select
                id="gen-format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="input w-full !py-2 text-[13px] cursor-pointer"
              >
                {formatOptions.map((f) => (
                  <option key={f} value={f} disabled={!allowedFormats.includes(f)}>
                    {f.toUpperCase()}
                    {!allowedFormats.includes(f) ? ' — upgrade to unlock' : ''}
                  </option>
                ))}
              </select>
              {lockedFormats.length > 0 && (
                <p className="m-0 mt-1 text-[11.5px] text-mist-500">
                  {lockedFormats.map((f) => f.toUpperCase()).join(' & ')} available on higher plans —{' '}
                  <a href="#billing" style={{ color: 'var(--a-hi)' }}>
                    upgrade
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Progress card */}
        {showProgress && (
          <div className="card p-[22px]">
            <div className="flex items-center gap-3 mb-3.5">
              <Spinner size={18} />
              <span className="text-mist-200 text-sm font-semibold">
                {currentJob?.status === 'processing' ? 'Generating audio…' : 'Queued…'}
              </span>
              <span className="ml-auto font-bold text-sm" style={{ color: 'var(--a-hi)' }}>
                {progress}%
              </span>
              {currentJob?.status === 'queued' && (
                <button
                  onClick={handleCancelJob}
                  disabled={cancelling}
                  className="btn-ghost !py-1.5 !px-3 !text-[12px] disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling…' : 'Cancel'}
                </button>
              )}
            </div>
            <div className="h-[9px] rounded-full bg-ink-700 overflow-hidden">
              <div
                className="grad-bar h-full rounded-full transition-[width] duration-200 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Poll timeout notice */}
        {pollTimedOut && (
          <div
            className="p-4 rounded-xl text-sm"
            style={{ background: 'rgba(224, 162, 31, .12)', color: '#E8C377' }}
          >
            <p className="font-semibold m-0">Still processing</p>
            <p className="mt-1 m-0">
              This is taking longer than usual. Your audio will show up in your{' '}
              <a href="#library" className="font-semibold underline" style={{ color: '#E8C377' }}>
                Library
              </a>{' '}
              shortly.
            </p>
          </div>
        )}

        {/* Success card */}
        {completed && currentJob?.audio_file && (
          <div
            className="fx p-[22px] rounded-2xl"
            style={{
              border: '1px solid var(--a-core)',
              background: 'linear-gradient(180deg, rgba(var(--a-rgb), .08), rgba(23,23,27,0))',
            }}
          >
            <audio
              key={currentJob.job_id}
              ref={genAudioRef}
              src={currentJob.audio_file.url}
              preload="metadata"
              onPlay={() => setGenPlaying(true)}
              onPause={() => setGenPlaying(false)}
              onEnded={() => {
                setGenPlaying(false);
                setGenTime(0);
              }}
              onTimeUpdate={(e) => setGenTime(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => {
                if (Number.isFinite(e.currentTarget.duration)) setGenDuration(e.currentTarget.duration);
              }}
            />
            <div className="flex items-center gap-2 mb-4 text-[13.5px] font-semibold" style={{ color: '#7BD0A0' }}>
              <Icon name="check" size={16} />
              Audio generated successfully
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleGenPlay}
                aria-label={genPlaying ? 'Pause audio' : 'Play audio'}
                className="w-[52px] h-[52px] rounded-full flex-none flex items-center justify-center cursor-pointer border-none"
                style={{
                  background: 'linear-gradient(135deg, var(--a-hi), var(--a-lo))',
                  boxShadow: '0 8px 22px rgba(var(--a-rgb), .3)',
                }}
              >
                <Icon name={genPlaying ? 'pause' : 'play'} size={20} fill="#0C0C0E" />
              </button>
              <div className="flex-1 min-w-0 relative overflow-hidden">
                <Waveform seed={currentJob.job_id || selectedVoice?.id || 'audio'} active big />
              </div>
              <span className="flex-none font-mono text-[13px] text-mist-400">
                {formatTime(genTime)} / {formatTime(displayDuration)}
              </span>
            </div>
            <div className="flex gap-3 mt-[18px]">
              <a href={currentJob.audio_file.url} download className="btn-grad inline-flex items-center gap-2">
                <Icon name="download" size={15} />
                Download
              </a>
              <button onClick={resetForAnother} className="btn-ghost">
                Generate another
              </button>
            </div>
          </div>
        )}

        {/* Failure alert */}
        {failed && (
          <div
            className="p-4 rounded-xl text-sm"
            style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}
          >
            <p className="font-semibold m-0">Generation failed</p>
            {currentJob?.error?.message && <p className="mt-1 m-0">{currentJob.error.message}</p>}
            <button
              onClick={handleGenerate}
              disabled={generating || !text.trim() || !selectedVoice}
              className="btn-ghost mt-3 !py-2 !px-4 !text-[12.5px] inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Icon name="refresh" size={14} />
              Try again
            </button>
          </div>
        )}

        {error && !currentJob && (
          <div
            className="p-4 rounded-xl text-sm"
            style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}
          >
            {error}
          </div>
        )}
      </div>

      {/* ------------------------------ Right column ----------------------------- */}
      <div className="flex flex-col gap-3.5">
        <div className="card p-[18px]">
          <div className="flex items-center justify-between gap-2 mb-3.5">
            <CardTitle>Select voice</CardTitle>
            <div className="flex items-center gap-2.5">
              <span className="text-mist-500 text-[12.5px]">{filteredVoices.length} available</span>
              <button
                onClick={() => {
                  setCloneError('');
                  setCloneOpen(true);
                }}
                className="btn-accent-outline !py-1.5 !px-3 !text-[12px] inline-flex items-center gap-1.5"
              >
                <Icon name="plus" size={13} />
                Clone voice
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mist-500 pointer-events-none">
              <Icon name="search" size={16} />
            </span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search voices…"
              className="input w-full pl-10 text-[13.5px]"
            />
          </div>

          {/* Language filter */}
          <div className="mb-3">
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              aria-label="Filter by language"
              className="input w-full !py-2.5 text-[13.5px] cursor-pointer"
            >
              <option value="">All languages</option>
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>

          {/* Provider chips */}
          <div className="flex gap-2 flex-wrap mb-3.5">
            <button
              onClick={() => setProviderFilter('')}
              className={providerFilter === '' ? 'chip chip-active' : 'chip'}
            >
              All
            </button>
            {providers.map((p) => (
              <button
                key={p}
                onClick={() => setProviderFilter(p)}
                className={providerFilter === p ? 'chip chip-active' : 'chip'}
              >
                {PROVIDER_LABELS[p] || p}
              </button>
            ))}
          </div>

          {previewError && (
            <p className="m-0 mb-2.5 text-xs" style={{ color: '#F0928C' }}>
              {previewError}
            </p>
          )}

          {/* Voice list */}
          <div className="flex flex-col gap-2.5 max-h-[460px] overflow-y-auto pr-1">
            {loading ? (
              <div className="flex justify-center py-6">
                <Spinner size={22} />
              </div>
            ) : voicesError ? (
              <div className="text-center py-6">
                <p className="text-sm m-0 mb-3" style={{ color: '#F0928C' }}>
                  {voicesError}
                </p>
                <button onClick={loadVoices} className="btn-ghost !py-2 !px-4 !text-[12.5px] inline-flex items-center gap-2">
                  <Icon name="refresh" size={14} />
                  Retry
                </button>
              </div>
            ) : filteredVoices.length === 0 ? (
              <p className="text-sm text-mist-500 text-center py-6 m-0">No voices found</p>
            ) : (
              filteredVoices.map((voice) => {
                const key = voiceKey(voice);
                const selected =
                  selectedVoice?.id === voice.id && selectedVoice?.provider === voice.provider;
                const playing = previewingId === key;
                const color = avatarColor(voice);
                const tone = voice.styles?.[0] || voice.age;
                return (
                  <div
                    key={key}
                    onClick={() => setSelectedVoice(voice)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedVoice(voice);
                      }
                    }}
                    className="flex flex-col gap-3 p-3.5 rounded-[14px] cursor-pointer transition-all duration-200"
                    style={{
                      border: '1px solid ' + (selected ? 'var(--a-core)' : '#26262B'),
                      background: selected ? 'rgba(var(--a-rgb), .08)' : '#17171B',
                      boxShadow: selected ? '0 8px 22px rgba(var(--a-rgb), .14)' : 'none',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-10 h-10 rounded-full flex-none flex items-center justify-center font-display font-extrabold text-sm text-ink-950"
                        style={{
                          background: `radial-gradient(120% 120% at 30% 20%, ${color} 0%, ${color}cc 60%, ${color}88 100%)`,
                          border: selected ? '2px solid var(--a-hi)' : '2px solid transparent',
                        }}
                      >
                        {voice.name.slice(0, 2).toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-[14.5px] truncate">{voice.name}</span>
                          {voice.is_custom ? (
                            <span
                              className="flex-none text-[10px] font-bold uppercase tracking-[.08em] px-1.5 py-0.5 rounded"
                              style={{ color: 'var(--a-hi)', background: 'rgba(var(--a-rgb), .12)' }}
                            >
                              My voice
                            </span>
                          ) : (
                            <span className="font-mono text-[11px] text-mist-500 flex-none">
                              {PROVIDER_LABELS[voice.provider] || voice.provider}
                            </span>
                          )}
                        </div>
                        <p className="m-0 mt-0.5 text-mist-400 text-xs">
                          {voice.is_custom ? 'Cloned voice' : `${voice.language} · ${voice.gender}`}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePreview(voice);
                        }}
                        disabled={previewLoading !== null && previewLoading !== key}
                        aria-label={playing ? `Pause ${voice.name} preview` : `Preview ${voice.name}`}
                        title={playing ? 'Pause preview' : 'Preview voice'}
                        className="w-[34px] h-[34px] rounded-full flex-none flex items-center justify-center cursor-pointer transition-all duration-200 disabled:opacity-40"
                        style={{
                          border: '1px solid ' + (selected || playing ? 'transparent' : '#34343B'),
                          background:
                            selected || playing
                              ? 'linear-gradient(135deg, var(--a-hi), var(--a-lo))'
                              : 'transparent',
                        }}
                      >
                        {previewLoading === key ? (
                          <Spinner size={13} />
                        ) : (
                          <Icon
                            name={playing ? 'pause' : 'play'}
                            size={13}
                            fill={selected || playing ? '#0C0C0E' : 'var(--a-core)'}
                          />
                        )}
                      </button>
                      {voice.is_custom && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteClone(voice);
                          }}
                          disabled={deletingCloneId !== null}
                          aria-label={`Delete ${voice.name}`}
                          title="Delete cloned voice"
                          className="flex-none text-mist-500 hover:text-[#F0928C] transition-colors disabled:opacity-40"
                        >
                          {deletingCloneId === voice.custom_voice_id ? <Spinner size={13} /> : <Icon name="trash" size={15} />}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2.5">
                      <div className="flex-1 overflow-hidden">
                        <Waveform seed={voice.id} active={selected} />
                      </div>
                      {tone && (
                        <span className="flex-none text-[11px] font-semibold uppercase tracking-[.06em] text-white">
                          {tone}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Generate CTA */}
        <button
          onClick={handleGenerate}
          disabled={!text.trim() || !selectedVoice || generating || overQuota}
          className="btn-grad w-full p-[15px] text-[13.5px]"
        >
          {generating ? 'Generating…' : overQuota ? 'Monthly limit reached' : 'Generate Audio'}
        </button>
      </div>

      {/* Clone voice modal */}
      {cloneOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
          onClick={closeCloneModal}
        >
          <div
            className="fx w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-ink-700 bg-ink-850 p-6 lg:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Clone a voice"
          >
            <h3 className="font-display font-extrabold text-white text-xl m-0 mb-1">Clone your voice</h3>
            <p className="text-mist-400 text-[13px] m-0 mb-5">
              Read the script below in a quiet room, or upload an existing recording. Your clone is private — only
              you can see and use it.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-4 mb-4">
              <div>
                <label className="label">Voice name</label>
                <input
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  maxLength={60}
                  placeholder="e.g. My narration voice"
                  className="input"
                />
              </div>
              <div>
                <label className="label">Language</label>
                <select
                  value={cloneLanguage}
                  onChange={(e) => changeCloneLanguage(e.target.value)}
                  className="input cursor-pointer"
                >
                  {CLONE_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reading script */}
            <div
              className="rounded-[14px] p-4 mb-4"
              style={{ border: '1px solid rgba(var(--a-rgb), .35)', background: 'rgba(var(--a-rgb), .06)' }}
            >
              <p className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[.1em]" style={{ color: 'var(--a-hi)' }}>
                Read this aloud ({REC_MIN_SECONDS}s–{REC_MAX_SECONDS}s)
              </p>
              {CLONE_SCRIPTS[cloneLanguage] ? (
                <p className="m-0 text-mist-200 text-[14px] leading-[1.75]">{CLONE_SCRIPTS[cloneLanguage]}</p>
              ) : (
                <p className="m-0 text-mist-300 text-[13.5px] leading-relaxed">
                  Speak naturally in your language for 30–120 seconds — describe your day, read a paragraph from a
                  book, vary your tone, and include a question and a pause.
                </p>
              )}
            </div>

            {/* Mode tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => {
                  setCloneMode('record');
                  setCloneError('');
                }}
                className={cloneMode === 'record' ? 'chip chip-active' : 'chip'}
              >
                🎙 Record now
              </button>
              <button
                onClick={() => {
                  setCloneMode('upload');
                  setCloneError('');
                }}
                className={cloneMode === 'upload' ? 'chip chip-active' : 'chip'}
              >
                Upload a file
              </button>
            </div>

            {cloneMode === 'record' ? (
              <div className="rounded-[14px] border border-ink-700 bg-ink-950 p-5 mb-4 flex flex-col items-center gap-3">
                {recState === 'idle' && (
                  <>
                    <button
                      onClick={startRecording}
                      className="w-16 h-16 rounded-full flex items-center justify-center cursor-pointer border-2 transition-transform hover:scale-105"
                      style={{ borderColor: '#D8362F', background: 'rgba(216,54,47,.14)' }}
                      aria-label="Start recording"
                    >
                      <span className="w-6 h-6 rounded-full" style={{ background: '#F0928C' }} />
                    </button>
                    <p className="m-0 text-mist-400 text-[13px]">Press to start recording, then read the script</p>
                  </>
                )}
                {recState === 'recording' && (
                  <>
                    <button
                      onClick={stopRecording}
                      className="w-16 h-16 rounded-full flex items-center justify-center cursor-pointer border-2 animate-pulse"
                      style={{ borderColor: '#D8362F', background: 'rgba(216,54,47,.25)' }}
                      aria-label="Stop recording"
                    >
                      <span className="w-5 h-5 rounded-[4px]" style={{ background: '#F0928C' }} />
                    </button>
                    <p className="m-0 font-mono text-[15px]" style={{ color: '#F0928C' }}>
                      ● {formatTime(recSeconds)} / {formatTime(REC_MAX_SECONDS)}
                    </p>
                    <p className="m-0 text-mist-500 text-xs">
                      Press the square to stop {recSeconds < REC_MIN_SECONDS ? `(minimum ${REC_MIN_SECONDS}s)` : ''}
                    </p>
                  </>
                )}
                {recState === 'processing' && (
                  <div className="flex items-center gap-3 py-4">
                    <Spinner size={18} />
                    <span className="text-mist-300 text-sm">Processing recording…</span>
                  </div>
                )}
                {recState === 'done' && recUrl && (
                  <>
                    <p className="m-0 text-[13.5px] font-semibold" style={{ color: '#7BD0A0' }}>
                      ✓ Recording ready ({formatTime(recSeconds)})
                    </p>
                    <audio controls src={recUrl} className="w-full max-w-sm" style={{ height: 40 }} />
                    <button onClick={resetRecording} className="btn-ghost !py-2 !px-4 !text-xs">
                      Re-record
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <label className="label">Audio sample (.mp3, .wav, .m4a, .opus — max 20 MB)</label>
                <input
                  type="file"
                  accept=".mp3,.wav,.m4a,.opus,audio/*"
                  onChange={(e) => setCloneFile(e.target.files?.[0] || null)}
                  className="block w-full text-[13px] text-mist-400 mb-3 file:mr-3 file:rounded-[9px] file:border file:border-ink-600 file:bg-transparent file:text-mist-200 file:px-3.5 file:py-2 file:text-[12.5px] file:font-semibold file:cursor-pointer"
                />
                <label className="label">What is said in the recording (optional — improves accuracy)</label>
                <textarea
                  value={cloneTranscript}
                  onChange={(e) => setCloneTranscript(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Type the exact words spoken in your audio file…"
                  className="input resize-y !text-[13px] leading-relaxed"
                />
              </div>
            )}

            <label className="flex items-start gap-2.5 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={cloneConsent}
                onChange={(e) => setCloneConsent(e.target.checked)}
                className="mt-0.5 accent-[var(--a-core)]"
              />
              <span className="text-mist-300 text-[12.5px] leading-relaxed">
                I confirm this is my own voice, or I have the speaker's explicit permission to clone it, and I won't
                use it to impersonate anyone.
              </span>
            </label>

            {cloneError && (
              <p className="m-0 mb-4 text-[12.5px]" style={{ color: '#F0928C' }}>
                {cloneError}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={closeCloneModal} disabled={cloneBusy} className="btn-ghost">
                Cancel
              </button>
              <button
                onClick={submitClone}
                disabled={cloneBusy || recState === 'recording' || recState === 'processing'}
                className="btn-grad inline-flex items-center gap-2"
              >
                {cloneBusy && <Spinner size={14} />}
                {cloneBusy ? 'Cloning…' : 'Clone voice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
